import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import posthog from "posthog-js";
import { ensureAnalyticsInitialized } from "../analytics";

export interface LauncherState {
  isShow: boolean;
  isOpen: boolean;
  lastOpen: number;
  openedOnce: boolean;
  isPreviewVisible: boolean;
  isExpanded: boolean;
  // User-set positioning and margin values
  sideMargin: string | null;
  bottomMargin: string | null;
  side: "left" | "right" | null;
}

const initialState: LauncherState = {
  isShow: true,
  isOpen: false,
  lastOpen: 0,
  openedOnce: false,
  isPreviewVisible: false,
  isExpanded: false,
  sideMargin: null,
  bottomMargin: null,
  side: null,
};

const setOpenAction = (state: any, action: PayloadAction<boolean>) => {
  const isOpen = action.payload;
  state.isOpen = isOpen;
  if (isOpen) {
    state.isShow = true;
    state.openedOnce = true;
    state.isPreviewVisible = false;
    state.lastOpen = performance.now();
  }
};

export const launcherSlice = createSlice({
  name: "launcher",
  initialState,
  reducers: {
    setShow: (state, action: PayloadAction<boolean>) => {
      state.isShow = action.payload;
    },
    setOpen: setOpenAction,
    setIsPreviewVisible: (state, action: PayloadAction<boolean>) => {
      state.isPreviewVisible = action.payload;
    },
    toggleLauncher: (state) => {
      const { isOpen, lastOpen } = state;
      const sincePageLoad = performance.now();

      // Initialize analytics on first user interaction
      ensureAnalyticsInitialized();

      if (isOpen) {
        posthog.capture("launcher_close", {
          sincePageLoad,
          duration: sincePageLoad - lastOpen,
        });
      } else {
        posthog.capture("launcher_open", { sincePageLoad });
        state.lastOpen = sincePageLoad;
      }
      setOpenAction(state, { payload: !isOpen, type: "_" });
    },
    toggleExpanded: (state) => {
      state.isExpanded = !state.isExpanded;
      ensureAnalyticsInitialized();
      posthog.capture("launcher_toggle_expanded", {
        isExpanded: state.isExpanded,
      });
    },
    setSideMargin: (state, action: PayloadAction<string | null>) => {
      state.sideMargin = action.payload;
    },
    setBottomMargin: (state, action: PayloadAction<string | null>) => {
      state.bottomMargin = action.payload;
    },
    setSide: (state, action: PayloadAction<"left" | "right" | null>) => {
      state.side = action.payload;
    },
  },
});

export const { setShow, setOpen, setIsPreviewVisible, toggleLauncher, toggleExpanded, setSideMargin, setBottomMargin, setSide } =
  launcherSlice.actions;
export const launcherReducer = launcherSlice.reducer;
